import { useQuery } from "@tanstack/react-query";
import { DashboardServices } from "../../../services/dashboardServices";
import { ProcesoConfirmacionInfo } from "./ProcesoConfirmacionInfo";
import { Text, TextLabel, TextChip, CircularProgress } from "../../../components/ui";
import { ApointmentStaus, appointmentStatusColors } from "../../../utilities/chipColorSchemas";
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import VaccinesRoundedIcon from '@mui/icons-material/VaccinesRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';

interface Props
{
    citaId: number;
}

export function CitaProcessInformation({ citaId }: Props) {
    const dashboardServices = new DashboardServices();

    const { data, isFetching, isError } = useQuery({
        queryKey: ["getProcessInformationByCitaId", citaId],
        queryFn: async () => await dashboardServices.getProcessInformationByCitaId(citaId),
    })

    const ivrIsCurrentProcess = data?.ivrProcessInfo?.procesoIniciado == true &&
        data?.messageProcessInfo?.procesoIniciado == false;
    const messageIsCurrentProcess = data?.messageProcessInfo?.procesoIniciado == true;

    const statusCitaColors = appointmentStatusColors[data?.statusCita as ApointmentStaus];

    if (isFetching)
        return (
        <div className="w-[900px] h-[616px]">
            <CircularProgress />
        </div>
        );

    if (isError)
        return (
        <div className="w-[900px] h-[616px]">
            Error al cargar la información, intente más tarde.
        </div>
        );

    return (
        <div className="w-[900px]">
            <div className="flex items-center mb-2 gap-2">
                <p className="text-xl font-semibold m-0">Detalles de la cita</p>

                <ArrowForwardIosRoundedIcon sx={{ width: 18, height: 18 }}/>

                <div>
                    <TextChip
                        text={data?.statusCita ?? "Sin status"}
                        borderColor={statusCitaColors?.borderColor}
                        bgColor={statusCitaColors?.bgColor}
                        textColor={statusCitaColors?.textColor}/>
                </div>
            </div>

            <div className="grid grid-cols-2 bg-slate-50 px-4 py-3 border border-slate-200 rounded-md">
                <div>
                    <div className="mb-3">
                        <TextLabel icon={<PersonRoundedIcon sx={{ width: 20, height: 20 }}/>}>Paciente</TextLabel>
                        <Text>{data?.paciente}</Text>
                    </div>

                    <div className="mb-3">
                        <TextLabel icon={<PhoneAndroidRoundedIcon sx={{ width: 20, height: 20 }}/>}>Telefono del paciente</TextLabel>
                        <Text>{data?.telefonoPaciente && data?.telefonoPaciente.length > 0
                            ? data?.telefonoPaciente
                            : "Sin telefono registrado"}</Text>
                    </div>

                    <div>
                        <TextLabel icon={<VaccinesRoundedIcon sx={{ width: 20, height: 20 }}/>}>Tratamiento</TextLabel>
                        <Text>{data?.tratamiento}</Text>
                    </div>
                </div>

                <div>
                    <div className="mb-3">
                        <TextLabel icon={<LocalHospitalRoundedIcon sx={{ width: 20, height: 20 }}/>}>Doctor de la cita</TextLabel>
                        <Text>{data?.doctor}</Text>
                    </div>

                    <div className="mb-3">
                        <TextLabel icon={<CalendarTodayRoundedIcon sx={{ width: 20, height: 20 }}/>}>Fecha de la cita</TextLabel>
                        <Text>{data?.fecha} {data?.horaInicio} - {data?.horaFin}</Text>
                    </div>

                    <div>
                        <TextLabel icon={<LocationCityRoundedIcon sx={{ width: 20, height: 20 }}/>}>Sucursal de la cita</TextLabel>
                        <Text>{data?.sucursal}</Text>
                    </div>
                </div>
            </div>

            <p className="text-xl font-semibold mt-3 mb-0">Processo de confirmación</p>

            {data?.ivrProcessInfo && (
            <ProcesoConfirmacionInfo
                processInfo={data?.ivrProcessInfo}
                isCurrentProcess={ivrIsCurrentProcess}/>
            )}

            {data?.messageProcessInfo && (
            <ProcesoConfirmacionInfo
                processInfo={data?.messageProcessInfo}
                isCurrentProcess={messageIsCurrentProcess}/>
            )}

        </div>
    );
}
